import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProduits } from '../context/ProduitContext';
import { useFavoris } from '../context/FavorisContext';
import Button from '../components/ui/Button';
import TraditionalPattern from '../components/TraditionalPattern';
import { getImageUrl as getImageUrlUtil } from '../utils/imageUtils';

const ProduitDetail = () => {
  const { id } = useParams();
  const { produit, loading, error, getProduit } = useProduits();
  const { isFavorite, toggleFavorite } = useFavoris();
  const [selectedImage, setSelectedImage] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  const getImageUrl = (photo) => {
    if (!photo) return '/images/placeholder-product.svg';
    return getImageUrlUtil(photo) || '/images/placeholder-product.svg';
  };

  const handleAddFavorite = async () => {
    const result = await toggleFavorite(produit._id);
    
    // Afficher une notification subtile
    setNotificationMessage(result.message);
    setShowNotification(true);
    
    // Masquer la notification après 2 secondes
    setTimeout(() => {
      setShowNotification(false);
    }, 2000);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/produits/${produit._id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: produit.nom, text: produit.description, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        alert('Lien copié dans le presse-papiers');
      } else {
        prompt('Copiez ce lien:', url);
      }
    } catch (err) {
      console.error('Erreur partage:', err);
    }
  };

  useEffect(() => {
    if (id) {
      getProduit(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const generateWhatsAppMessage = () => {
    const produitUrl = `${window.location.origin}/produits/${produit?._id}`;
    const message = `Bonjour, je veux commander ce produit: ${produit?.nom} - ${produit?.prix}F CFA\n\nLien du produit: ${produitUrl}`;
    return `https://wa.me/${produit?.artisan?.whatsapp?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary-800 mb-4"></i>
          <p className="text-gray-600">Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (!produit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          {error ? (
            <>
              <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
              <p className="text-gray-600 mb-4">{error}</p>
            </>
          ) : (
            <>
              <i className="fas fa-exclamation-triangle text-4xl text-yellow-500 mb-4"></i>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Produit non trouvé</h2>
            </>
          )}
          <Link to="/produits" className="text-primary-800 hover:text-primary-600 font-semibold">
            Retour aux produits
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 relative transition-colors duration-300">
      <TraditionalPattern variant="circles" opacity={0.05} className="text-primary-600 dark:text-primary-400 fixed inset-0" />
      
      {/* Notification subtile pour les favoris */}
      {showNotification && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 bg-white dark:bg-gray-800 px-6 py-3 rounded-full shadow-xl border-2 border-accent-400 dark:border-accent-500 animate-fade-in">
          <span className="text-sm font-semibold text-primary-900 dark:text-primary-300 flex items-center gap-2">
            <i className={`fas fa-heart ${isFavorite(produit?._id) ? 'text-red-500' : 'text-gray-400 dark:text-gray-600'}`}></i>
            {notificationMessage}
          </span>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li><Link to="/" className="text-primary-800 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300">Accueil</Link></li>
            <li><i className="fas fa-chevron-right text-gray-400 dark:text-gray-600"></i></li>
            <li><Link to="/produits" className="text-primary-800 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300">Produits</Link></li>
            <li><i className="fas fa-chevron-right text-gray-400 dark:text-gray-600"></i></li>
            <li className="text-gray-600 dark:text-gray-400">{produit.nom}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Images */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 shadow">
              <img
                src={getImageUrl(produit.photos?.[selectedImage])}
                alt={produit.nom}
                className="w-full h-[560px] object-cover sm:object-contain"
                onError={(e) => { e.target.src = '/images/placeholder-product.jpg'; }}
              />
            </div>

            {produit.photos?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto no-scrollbar py-2">
                {produit.photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden border-2 ${selectedImage === index ? 'border-accent-500 ring-2 ring-accent-200 dark:ring-accent-700' : 'border-gray-200 dark:border-gray-700'} focus:outline-none`}
                    aria-label={`Voir l'image ${index + 1}`}
                  >
                    <img
                      src={getImageUrl(photo)}
                      alt={`${produit.nom} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Informations produit */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-gray-100 mb-2">
                {produit.nom}
              </h1>
              <div className="flex items-center space-x-2 mb-4">
                <span className="bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-300 px-3 py-1 rounded-full text-sm">
                  {produit.categorie}
                </span>
                {produit.pourcentageReduction > 0 && (
                  <span className="bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 px-3 py-1 rounded-full text-sm font-semibold">
                    -{produit.pourcentageReduction}%
                  </span>
                )}
              </div>
            </div>

            {/* Prix */}
            <div className="flex items-center space-x-4">
              {produit.pourcentageReduction > 0 ? (
                <>
                  <span className="text-3xl font-extrabold text-red-600 dark:text-red-400">
                    {produit.prix}F CFA
                  </span>
                  <span className="text-lg text-gray-500 dark:text-gray-400 line-through">
                    {produit.prixOriginal}F CFA
                  </span>
                  <span className="ml-2 text-sm px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full">-{produit.pourcentageReduction}%</span>
                </>
              ) : (
                <span className="text-3xl font-extrabold text-primary-800 dark:text-primary-400">
                  {produit.prix}F CFA
                </span>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Description</h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{produit.description}</p>
            </div>

            {/* Détails */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              {produit.materiaux?.length > 0 && (
                <div>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Matériaux:</span>
                  <p className="text-gray-600 dark:text-gray-300">{produit.materiaux.join(', ')}</p>
                </div>
              )}
              {produit.dimensions && (
                <div>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Dimensions:</span>
                  <p className="text-gray-600 dark:text-gray-300">
                    {produit.dimensions.longueur}×{produit.dimensions.largeur}×{produit.dimensions.hauteur} {produit.dimensions.unite}
                  </p>
                </div>
              )}
            </div>

            {/* Artisan */}
            {produit.artisan && (
              <div className="border-t dark:border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Artisan</h3>
                <Link
                  to={`/artisans/${produit.artisan._id}`}
                  className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 hover:shadow-md transition-shadow"
                >
                  <img
                    src={getImageUrl(produit.artisan.photoProfil)}
                    alt={produit.artisan.nom}
                    className="w-16 h-16 rounded-full object-cover"
                    onError={(e) => {
                      e.target.src = '/images/placeholder-artisan.svg';
                    }}
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">{produit.artisan.nom}</h4>
                    <p className="text-gray-600 dark:text-gray-300">{produit.artisan.specialite}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{produit.artisan.region}</p>
                  </div>
                </Link>
              </div>
            )}

            {/* Actions - sticky on large screens handled by layout */}
            <div className="pt-6">
              <div className="flex items-center gap-3">
                <a 
                  href={generateWhatsAppMessage()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-green-300"
                  aria-label="Commander via WhatsApp"
                >
                  <i className="fab fa-whatsapp text-xl" aria-hidden="true"></i>
                  Commander via WhatsApp
                </a>

                <Button 
                  variant="icon" 
                  size="icon" 
                  onClick={handleAddFavorite} 
                  ariaLabel={isFavorite(produit._id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                  className={isFavorite(produit._id) ? 'text-red-500 border-red-500 hover:bg-red-50' : 'hover:text-red-500 hover:border-red-500'}
                >
                  <i className={isFavorite(produit._id) ? 'fas fa-heart' : 'far fa-heart'} aria-hidden="true"></i>
                </Button>

                <Button variant="icon" size="icon" onClick={handleShare} ariaLabel="Partager produit">
                  <i className="fas fa-share-alt" aria-hidden="true"></i>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProduitDetail;